import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ReceiptIcon as Receipt, XIcon as X, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Year-to-date 1099-K informational banner.
 *
 * Render rules (kept deliberately quiet — this is informational, not
 * a nag):
 *   • Logged-in user only.
 *   • Hits /api/me/tax/ytd-summary once per session.
 *   • Renders only when `gross_usd > 0` (user has received at least
 *     one payout this calendar year). Hides for buyers, non-sellers,
 *     and brand-new accounts.
 *   • One per calendar year. Dismissal is persisted in localStorage
 *     under `tax_1099k_banner_dismissed_<year>` so it doesn't come
 *     back next render but DOES come back on Jan 1.
 *   • Above-the-fold above page content inside <main> so sellers see
 *     it the first time they land in a dashboard each year — but
 *     does not block layout below.
 *
 * Why no API-side dismiss persistence: this banner is informational
 * not transactional. localStorage is fine; if a user clears their
 * browser, seeing the banner again once isn't a real harm. The page
 * it links to is the source of truth anyway.
 */
export default function Tax1099KBanner() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(true); // start dismissed so we don't flash

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const year = new Date().getUTCFullYear();
    const dismissKey = `tax_1099k_banner_dismissed_${year}`;
    if (typeof window !== "undefined" && window.localStorage?.getItem(dismissKey)) {
      return; // stays dismissed
    }
    api
      .get("/me/tax/ytd-summary")
      .then(({ data }) => {
        if (cancelled || !data) return;
        if (Number(data.gross_usd) > 0) {
          setSummary(data);
          setDismissed(false);
        }
      })
      .catch(() => {
        /* silent — banner is informational */
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  if (dismissed || !summary) return null;

  const year = summary.year;
  const dismissKey = `tax_1099k_banner_dismissed_${year}`;
  const gross = Number(summary.gross_usd || 0);
  const likely = !!summary.likely_1099k_year;

  const onDismiss = () => {
    try {
      window.localStorage?.setItem(dismissKey, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-gold/40 bg-gradient-to-r from-[#FBF7EC] to-white p-4 sm:p-5 flex items-start gap-3"
      data-testid="tax-1099k-banner"
    >
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gold/40 flex-shrink-0">
        <Receipt size={20} weight="duotone" className="text-gold-dark" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gold-dark mb-0.5">
          Heads up · {year} tax season
        </p>
        <p className="text-[13.5px] text-ink leading-snug">
          {likely ? (
            <>
              You&rsquo;ve received <strong>${gross.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>{" "}
              in payouts this year &mdash; that&rsquo;s past the federal $5,000 threshold, so{" "}
              <strong>Stripe will file a 1099-K for your account in January {year + 1}</strong>.
              You&rsquo;ll download it from your Stripe Express dashboard.
            </>
          ) : (
            <>
              You&rsquo;ve received <strong>${gross.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>{" "}
              in payouts so far this year. If you cross the federal $5,000 threshold (lower in several states),{" "}
              <strong>Stripe will issue your 1099-K</strong> directly from your Express dashboard &mdash; not Armstrong.
            </>
          )}
        </p>
        <Link
          to="/help/1099-k-and-taxes"
          className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-crimson hover:underline"
          data-testid="tax-1099k-banner-learn-more"
        >
          How 1099-K works on Armstrong K9 Registry
          <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-[#9A9A9A] hover:text-ink p-1 flex-shrink-0"
        aria-label="Dismiss tax notice for this year"
        data-testid="tax-1099k-banner-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
